import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormBySchema, FormFieldLayout, Item, useAPIWrapper } from 'drf-react-by-schema';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'src/app/hooks';
import { appDispatch } from 'src/utils/utils';
import { setFormIsValid, setHandleSubmit, setPage } from 'src/features/pageSlice';
import { PageType } from 'src/constants/PageType';
import { Perfil } from 'src/constants/Perfil';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';
import { GENERAL_ADMINS } from 'src/constants/Permission';
import { formLayout } from 'src/styles/layout';

const MODEL = 'perfil';
// const key = MODEL;

type WizardOptions = {
  FIELD_LAYOUT: FormFieldLayout;
  AUTOFOCUS_FIELD?: string;
  DEFAULTS?: Item;
};

function objectReducer(array: WizardOptions[], key: keyof WizardOptions) {
  if (key === 'FIELD_LAYOUT') {
    return array.map(({ FIELD_LAYOUT }) => FIELD_LAYOUT);
  }
  let reduced: Item = {};
  for (const item of array) {
    if (typeof item[key] === 'object') {
      reduced = {
        ...reduced,
        ...item[key],
      };
    }
  }
  return reduced;
}

export default function PerfilForm() {
  const { perfil, formValues, formWizard, type, key, id, isMobile } = useAppSelector(
    (state) => state.page
  );
  const { pageForm, loadSinglePageData, onEditModel } = useAPIWrapper();
  const [URLSearchParams] = useSearchParams();
  const page = parseInt(URLSearchParams.get('page') || '1');
  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
    register,
    handleSubmit,
  } = useForm({
    mode: 'onBlur',
    resolver: pageForm?.validationSchema ? yupResolver(pageForm.validationSchema) : undefined,
  });
  const [isAdmin] = useHasPermissionsMulti([GENERAL_ADMINS]);

  const hiddenFields = ['id', 'tipo', 'first_run'];

  const formValuesId = useMemo(() => {
    if (!key || !perfil?.id) {
      return;
    }
    return key === 'perfil' ? perfil.id : id;
  }, [key, id, perfil?.id]);

  const formValuesPerfil = useMemo(() => {
    if (!key || !perfil?.tipo) {
      return;
    }
    return key === 'perfil' ? perfil?.tipo : (formValues?.tipo?.value as Perfil | undefined);
  }, [key, perfil?.tipo, formValues?.tipo?.value]);

  const wizardOptions = useMemo(() => {
    const dadosPessoais = {
      FIELD_LAYOUT: {
        title: 'Dados pessoais',
        rows: [
          'nome',
          'apelido',
          ...(isAdmin ? ['cpf'] : []),
          'data_nascimento',
          'telefone',
          'telefone_adicional',
          'foto',
        ],
      },
      AUTOFOCUS_FIELD: undefined,
      DEFAULTS: { tipo: Perfil.QUEBRADEIRA },
    };

    if (!formValuesPerfil) {
      return [dadosPessoais];
    }

    switch (formValuesPerfil) {
      case Perfil.QUEBRADEIRA:
      case Perfil.DIRECAO:
        return [
          dadosPessoais,
          {
            FIELD_LAYOUT: {
              title: 'Território e grupo',
              rows: ['comunidade', 'grupo', ...(isAdmin ? ['is_coordinator_grupo'] : [])],
            },
            AUTOFOCUS_FIELD: undefined,
            DEFAULTS: undefined,
          },
          {
            FIELD_LAYOUT: {
              title: key === 'gerenciar_usuarias' ? 'Na ASMUBIP' : 'Você na ASMUBIP',
              rows: [
                'idade_inicio_atividade',
                'local_coleta',
                'distancia_local_coleta',
                ...(isAdmin
                  ? ['is_coordinator_grupo', 'is_associated_asmubip', 'data_filiacao']
                  : []),
              ],
            },
            AUTOFOCUS_FIELD: undefined,
            DEFAULTS: undefined,
          },
        ];
      case Perfil.ENTREPOSTO:
        return [
          dadosPessoais,
          {
            FIELD_LAYOUT: {
              title: 'Entreposto',
              rows: ['entreposto', ...(isAdmin ? ['is_manager_entreposto'] : [])],
            },
            AUTOFOCUS_FIELD: undefined,
            DEFAULTS: undefined,
          },
        ];
      case Perfil.ASSESSORIA:
        return [
          dadosPessoais,
          {
            FIELD_LAYOUT: {
              title: 'Entidade',
              rows: ['entidade_assessoria'],
            },
            AUTOFOCUS_FIELD: undefined,
            DEFAULTS: undefined,
          },
        ];
      case Perfil.PARCEIRO:
        return [
          dadosPessoais,
          {
            FIELD_LAYOUT: {
              title: 'Parceria',
              rows: ['entidade_assessoria', 'tipo_parceiro'],
            },
            AUTOFOCUS_FIELD: undefined,
            DEFAULTS: undefined,
          },
        ];
      case Perfil.FAMILIAR:
        return [
          dadosPessoais,
          {
            FIELD_LAYOUT: {
              title: 'Referência',
              rows: ['quebradeira_referencia'],
            },
            AUTOFOCUS_FIELD: undefined,
            DEFAULTS: undefined,
          },
        ];
      case Perfil.SUPERADMIN:
        return [dadosPessoais];
    }
  }, [formValuesPerfil, key, isAdmin]);

  const fieldsLayout = useMemo(() => {
    if (!key || !formValuesPerfil) {
      return;
    }
    return isMobile
      ? [wizardOptions[page - 1].FIELD_LAYOUT]
      : (objectReducer(wizardOptions, 'FIELD_LAYOUT') as FormFieldLayout[]);
  }, [key, formValuesPerfil, page, isMobile, wizardOptions]);

  const autoFocusField = useMemo(() => {
    if (!key || !formValuesPerfil) {
      return;
    }
    return isMobile ? wizardOptions[page - 1].AUTOFOCUS_FIELD : wizardOptions[0].AUTOFOCUS_FIELD;
  }, [key, formValuesPerfil, page, isMobile, wizardOptions]);

  useEffect(() => {
    if (!key || !fieldsLayout || !formValuesId) {
      return;
    }
    const loadAllDataAsync = async () => {
      const thisPageWizardOptions = wizardOptions[page - 1];

      const values = await loadSinglePageData({
        model: MODEL,
        objId: formValuesId,
        defaults: !thisPageWizardOptions
          ? undefined
          : isMobile
            ? thisPageWizardOptions.DEFAULTS
            : objectReducer(wizardOptions, 'DEFAULTS'),
        fieldsLayout,
      });

      if (values) {
        const pageTitle =
          values.first_run === true
            ? `Olá ${values.apelido || values.nome}! Complete seu cadastro`
            : key === 'gerenciar_usuarias'
              ? `Editar usuária "${values.nome}"`
              : 'Seu perfil';
        if (values.first_run) {
          values.first_run = false;
        }

        // Populate the form
        reset(values);

        appDispatch(
          setPage({
            model: MODEL,
            title: pageTitle,
            formValues: values,
            type: PageType.EDIT,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [page, isMobile, formValuesPerfil, formValuesId, wizardOptions, key, fieldsLayout]);

  useEffect(() => {
    if (!key || !type || type !== PageType.EDIT || !formValuesId || !formValuesPerfil) {
      return;
    }
    if (!formWizard) {
      const newSubmitSuccessTarget = `/${key}/view/${formValuesId}`;
      const newFormWizard = {
        pages: wizardOptions.length,
        submitSuccessTarget: newSubmitSuccessTarget,
      };
      appDispatch(
        setPage({ formWizard: newFormWizard, submitSuccessTarget: newSubmitSuccessTarget })
      );
    }
  }, [formWizard, type, key, formValuesId, formValuesPerfil, wizardOptions]);

  useEffect(() => {
    appDispatch(
      setPage({
        formDisabled: !isDirty,
      })
    );
  }, [isDirty]);

  useEffect(() => {
    appDispatch(setFormIsValid(isValid));
  }, [isValid]);

  useEffect(() => {
    appDispatch(setHandleSubmit(handleSubmit));
  }, [handleSubmit]);

  if (!pageForm?.schema || !formValues || !type || !fieldsLayout) {
    return null;
  }

  return (
    <FormBySchema
      fieldsLayout={fieldsLayout}
      autoFocusField={autoFocusField}
      hiddenFields={hiddenFields}
      control={control}
      register={register}
      errors={errors}
      schema={pageForm.schema}
      onEditModel={onEditModel}
      getValues={getValues}
      setValue={setValue}
      elevation={0}
      {...formLayout}
    />
  );
}
